import { Box, H2 } from '@ouster/webviz-react-components';

import { Tabs, Tab } from '@mui/material';
import { useEffect, useState, useCallback } from 'react';
import { PiFolderLockFill } from 'react-icons/pi';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useStoreState } from '../reducers';

const tabConfig = [
  { path: '/admin_dashboard/user', label: 'User Management' },
  { path: '/admin_dashboard/recording', label: 'Recording Lookup' },
  {
    path: '/admin_dashboard/recordings_management',
    label: 'Recordings Management',
  },
  { path: '/admin_dashboard/org', label: 'Organization Overview' },
];

export default function AdminDashboardLayout() {
  const { curUserOrg } = useStoreState((storeState) => storeState.user);
  const location = useLocation();
  const navigate = useNavigate();

  // Add more tabs here as needed

  const getTabIndex = useCallback(() => {
    if (
      location.pathname.startsWith('/admin_dashboard/recordings_management')
    ) {
      return 2; // Index for Recordings Management
    }
    if (location.pathname.startsWith('/admin_dashboard/org')) {
      return 3; // Index for Organization Overview
    }

    const matchedIndex = tabConfig.findIndex((tab) =>
      location.pathname.startsWith(tab.path)
    );
    return matchedIndex !== -1 ? matchedIndex : 0;
  }, [location.pathname]);

  const [tabIndex, setTabIndex] = useState(getTabIndex());

  useEffect(() => {
    setTabIndex(getTabIndex());
  }, [getTabIndex, location.pathname]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    navigate(tabConfig[newValue].path);
  };

  /* 
    Stay loading if didn't find user's organization
  */
  if (!curUserOrg) return null;

  if (curUserOrg.id !== 1 || curUserOrg.role !== 'ADMIN') {
    return (
      <Box
        fontSize={24}
        w="100%"
        h="100%"
        display="grid"
        placeContent="center"
        rowGap={8}
        justifyItems="center"
        data-testid="admin-dashboard-permission-denied"
      >
        <p>{`Sorry, you don't have the permission to view this page`}</p>
        <PiFolderLockFill size={72} />
      </Box>
    );
  }

  return (
    <Box
      height="100%"
      overflow="hidden"
      alignContent="start"
      p="30px 26px 36px"
      display="grid"
      gridTemplateRows="auto auto 1fr"
      data-testid="admin-dashboard-root"
    >
      <H2 mb={4}>Admin Dashboard</H2>
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        data-testid="admin-dashboard-tabs"
      >
        {tabConfig.map((tab, index) => (
          <Tab
            key={tab.path}
            label={tab.label}
            value={index}
            data-testid={`admin-dashboard-tab-${tab.label
              .replace(/\s+/g, '-')
              .toLowerCase()}`}
          />
        ))}
      </Tabs>
      <Box overflowY="auto" pr={6} data-testid="admin-dashboard-content">
        <Outlet />
      </Box>
    </Box>
  );
}
